// The New Item wizard. One component, every lane that creates a catalog item.
//
// ── WHY IT IS TWO STEPS AND NOT ONE LONGER FORM ─────────────────────────────
// This used to be a single form that asked for a SKU first, in a grammar the
// person had to know, and then eleven more things. Two problems, both real:
// people hated composing SKUs, and the form asked a grommet all the questions a
// circuit board has.
//
// Step one asks the one question whose answer changes the rest: what sort of
// thing is this. Step two is the questions that KIND actually has. That is a
// branch, not pagination, which is the only reason a step indicator is honest
// here — it names a decision that reshapes what comes next, rather than
// counting screens.
//
// ── TWO SHAPES BEHIND THE ONE DOOR ──────────────────────────────────────────
// A BOARD (`item_kinds.code_mode = 'structured'`) keeps the full grammar,
// because every segment is a decision somebody made and the server parses it to
// resolve a part family. The SKU builder appears with its Board segment already
// answered by step one.
//
// EVERYTHING ELSE (`short`) has no SKU field at all. The server assigns
// `GMT-7K3Q` on create: the kind's stem, a dash, four characters. The name is
// what people type and what every other screen shows, so the name is what this
// asks for.
//
// ── NOBODY LEAVES ───────────────────────────────────────────────────────────
// Per the standing rule in web/CLAUDE.md, everything the current task needs is
// reachable from where the person stands. A kind that does not exist yet is
// created from step one (`KindPicker`'s pinned row, which mounts
// `ItemKindForm`); a PCBA vendor that does not exist yet
// is created from the manufacturer segment (`VendorForm`, embedded). Both land
// straight back in the field that asked for them. Walking to an admin page with
// a half-filled item on screen is how a half-filled item gets abandoned.
//
// ── AI PREFILL, ON ONE SHAPE ────────────────────────────────────────────────
// Drop a datasheet PDF, paste a product link, or paste page text; POST
// /api/procurement/item-prefill proposes description/MPN/manufacturer/
// consumable with per-field confidence. It is on the short branch only, which
// is the same boundary the endpoint itself draws: boards never enter that lane
// (see item-prefill-router.js for why).
//
// Per-field treatment mirrors the receipts-digitizer contract the shop already
// knows: confident + grounded fields fill normally; low-confidence or
// ungrounded fields fill but get an amber "worth double-checking" note; fields
// the model declined (`not_sure`) stay EMPTY with a "the datasheet doesn't
// say" note. Nothing here auto-submits — every field is still a normal
// editable input, and Create is still a deliberate press.
//
// ── TWO CLASSIFICATION AXES, BOTH REQUIRED ──────────────────────────────────
// material_type says WHERE in the build chain a thing sits (SAP's standard
// vocabulary, code in the label). commodity_class says WHAT KIND of thing it
// is. Both open unanswered and Create refuses until both are given, naming
// whichever is missing. Migration 016 dropped the DB defaults for the same
// reason: a default is a classification nobody made, which is exactly how all
// 33 catalog rows ended up as 'component'.
//
// ONE exception, and it is an entailment rather than a default: a structured
// kind IS an assembled board, so commodity_class opens on 'pcba' for a board.
// material_type is left to the human, because a board can be a bought-in
// trading good or something we make.
//
// The AI prefill SUGGESTS on the other axis: a successful read means this
// lane's raw material, so it selects material_type 'raw_material' and maps a
// commodity_class from what the model said. Suggestions PRESELECT the pickers,
// they do not replace them, and they never overwrite an answer a human already
// gave.
//
// ── MANUFACTURER + MPN ───────────────────────────────────────────────────────
// The two sit side by side because they are ONE fact in two halves.
// "RC0402FR-0710KL" is ambiguous; "Yageo RC0402FR-0710KL" is a part you can buy.
// This is ERPNext's default_item_manufacturer / default_manufacturer_part_no
// pair: the USUAL manufacturer, not the only possible one.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { SkeletonGroup, Skeleton } from '../components/ui/Skeleton';
import { useApi } from '../apiHelpers';
import Dialog from '../components/ui/Dialog';
import KindPicker from './KindPicker';
import { VendorForm, type VendorFullRow } from '../Admin/VendorsAdmin';
import type { VendorTypeOption } from '../Procurement/vendorTypeLabels';
import type { PartsCatalogRow, MaterialType, CommodityClass } from '../Receiving/types';
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
} from './classificationDisplay';
import ClassificationGuideButton from './ClassificationGuide';
import { confidenceTier } from '../components/review/confidence';
import { modalLabel as labelClass } from '../components/review/fieldStyles';
import { Checkbox } from '../components/ui/Checkbox';
import DropZone from '../components/ui/DropZone';
import { Field, TextArea } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import { Select } from '../components/ui/Select';
import { exampleCode, suggestCommodityClass, previewBoardSku, type BoardFields } from './skuGrammar';
import useItemKindsStore, { kindByKey } from '../stores/useItemKindsStore';
import ItemName, { type ItemNameSource } from '../components/ItemName';
import SkuBuilder, { emptyBoard, emptyNonBoard, skuIsWellFormed } from './SkuBuilder';

interface PrefillField {
  value: string | boolean | null;
  confidence: number;
  grounded?: boolean;
}

interface ItemPrefillResult {
  item_type: 'raw_material';
  source: 'pdf' | 'url' | 'text';
  fields: {
    description: PrefillField;
    mpn: PrefillField;
    manufacturer: PrefillField;
    is_consumable: PrefillField;
  };
  not_sure: string[];
  warnings: string[];
}

/**
 * A row from GET /parts-catalog/sku-siblings.
 *
 * The name fields are the point: this panel asks "is one of these the item you
 * mean", and a person answers that by reading names, which is why the endpoint
 * left-joins `catalog_item_names` and why `ItemName` renders these rows. Typing
 * it as sku + description would have made the component's own renderer look
 * wrong rather than the type.
 *
 * `exact` is the endpoint's own verdict: same stem, tails equal as numbers.
 */
interface SkuSibling extends ItemNameSource {
  id: number;
  sku: string;
  description: string;
  exact: boolean;
}

/** The server's own limit on parts_catalog.name. */
const NAME_MAX = 60;

/**
 * The kind that exists so a one-off does not need a kind of its own. Picking
 * it opens the Prototype question already answered, because answering it twice
 * is the only thing choosing this kind could have meant.
 *
 * A key, not a code_mode: prototype-ness is a flag any item may carry, and
 * this kind is only the shortcut for a part that is nothing else.
 */
const PROTOTYPE_KIND = 'PRT';

/**
 * The loose SKU shape is not reachable from this wizard, so the builder's
 * non-board props are satisfied once at module scope rather than with a state
 * hook that models a screen nobody can get to. A non-board item takes a
 * generated short code now; that is the whole point of the branch.
 */
const NON_BOARD_UNUSED = emptyNonBoard();
const noop = () => {};

// "Worth double-checking" is exactly the review kit's own bar: anything that
// drops out of its 'high' tier (>= 0.85). One vocabulary for AI confidence
// across procurement, defined once in components/review/confidence.
function fieldNeedsReview(f?: PrefillField): boolean {
  if (!f || f.value === null || f.value === undefined) return false;
  if (f.grounded === false) return true;
  return confidenceTier(f.confidence) !== 'high';
}

// Renders under a field: the "doesn't say" note when the model declined it,
// or the amber "double-check" note when it answered but isn't confident.
// Neither, once the human has looked and the field is just... filled in.
const FieldReviewNote: React.FC<{
  notSure: boolean;
  needsReview: boolean;
  /** Overrides the generic "doesn't say" line where the model can be specific
   *  about WHY. Manufacturer is the case that earns it: the prompt teaches the
   *  distributor-is-not-the-manufacturer rule, so "not sure" there usually
   *  means it found a Mouser, and saying so tells the user where to look. */
  notSureText?: string;
}> = ({ notSure, needsReview, notSureText }) => {
  if (notSure) {
    return (
      <p className="text-[10px] text-on-surface-variant mt-1">
        {notSureText || "The datasheet doesn't say."} Fill this in.
      </p>
    );
  }
  if (needsReview)
    return <p className="text-[10px] text-amber-400/90 mt-1">Worth double-checking against the source.</p>;
  return null;
};

/**
 * The AI lane returns description / MPN / manufacturer / consumable, and never
 * a commodity class — see the hard-strip contract in item-prefill-ai.js. So the
 * mapping is OURS and it is deliberately narrow: only the one reading this lane
 * can actually justify.
 *
 * A datasheet the model could read at all is a commercial part, and a
 * consumable commercial part in this shop is bulk stock (wire, filament,
 * adhesive) far more often than anything else. Everything else stays UNSET so
 * the human picks, because a wrong preselection is worse than an empty one: it
 * is a wrong answer that looks reviewed.
 */
function commodityClassFromPrefill(result: ItemPrefillResult): CommodityClass | '' {
  return result.fields.is_consumable.value === true ? 'material' : '';
}

/**
 * The name a board would get if nobody typed one, composed client-side from the
 * same segments the SKU is.
 *
 * "Gen2 Pyro (V24, rev 1, Green Circuits, prototype)" — the thing, then which
 * one of the things. `Gen{N}` is the DRONE generation the board is for, never
 * the board's own version; that is the revision, and it is inside the
 * parentheses where the rest of the variance lives.
 *
 * Returned in TWO halves, because they are two facts and a typed name replaces
 * only the first: "Gen2 Pyro" is what the thing is, "V24, rev 1, Green
 * Circuits, prototype" is which one of them. Naming it "Big Pyro" gives
 * "Big Pyro (V24, rev 1, Green Circuits, prototype)", which is exactly how the
 * server composes display_name.
 *
 * Returns null until there is enough to say anything, so the preview stays
 * quiet rather than showing half a sentence.
 */
function previewGeneratedName(
  board: BoardFields,
  kindLabel: string | null,
  vendorName: string | null
): { base: string; detail: string | null } | null {
  const kind = kindLabel || board.board;
  if (!kind) return null;
  const gen = parseInt(board.generation, 10);
  // "Gen02" is the SKU's spelling, not a person's.
  const base = Number.isFinite(gen) ? `Gen${gen} ${kind}` : kind;
  const rev = board.revMinor ? `${board.revMajor}.${board.revMinor}` : board.revMajor;
  const detail = [
    board.variant ? `V${board.variant}` : null,
    board.revMajor ? `rev ${rev}` : null,
    vendorName,
    board.prototype ? 'prototype' : null,
  ].filter(Boolean);
  return { base, detail: detail.length ? detail.join(', ') : null };
}

/** The two halves, joined the way the catalog's display_name is. */
const joinName = (base: string, detail: string | null) =>
  detail ? `${base} (${detail})` : base;

type PrefillTab = 'pdf' | 'url' | 'text';

/**
 * THE ONE STRUCTURAL DEVICE ON THIS SURFACE, and it encodes something true.
 *
 * Two cells because there are two steps, and the first cell keeps showing its
 * ANSWER once it has one. That is the reason it exists rather than a dot row:
 * the kind is what step two is shaped by, so it has to stay on screen, and the
 * place it belongs is the thing that asked for it. It doubles as the way back,
 * which is why the answered cell is a button.
 */
const StepRail: React.FC<{
  step: 1 | 2;
  answer: string | null;
  /** Which code style the answer implies, which is the half of it that decides
   *  what step two looks like. Not the example code: step two already shows the
   *  real one, live, and a rail that repeats it is decoration. */
  answerNote: string | null;
  onBack: () => void;
}> = ({ step, answer, answerNote, onBack }) => {
  const cell = (active: boolean) =>
    `flex-1 min-w-0 rounded-lg px-3 py-2 text-left transition-colors duration-200 ease-emphasized motion-reduce:transition-none ${
      active
        ? 'bg-primary/10 ring-1 ring-inset ring-primary/40'
        : 'bg-surface-container-low'
    }`;
  const num = (active: boolean) =>
    `font-mono text-xs tabular-nums ${active ? 'text-primary-ink' : 'text-on-surface-variant'}`;
  const title = (active: boolean) => `text-xs ${active ? 'text-on-surface' : 'text-on-surface-variant'}`;

  return (
    <ol className="flex items-stretch gap-2 mb-4">
      <li className="flex-1 min-w-0 flex">
        {step === 2 ? (
          <button type="button" onClick={onBack} className={`${cell(false)} hover:bg-surface-container`}>
            <span className="flex items-baseline gap-2">
              <span className={num(false)}>1</span>
              <span className={title(false)}>What is it</span>
            </span>
            {/* The answer, kept where the question was asked, landing once as
                it arrives. This is the surface's one motion moment, and it is
                here rather than on the form because a filled `animate-*` leaves
                a transform behind, and a transformed ancestor breaks every
                `fixed` child under it. Nothing fixed lives in the rail. */}
            <span className="block truncate text-sm text-on-surface mt-0.5 animate-rowLand motion-reduce:animate-none">
              {answer || 'Not chosen'}
              {answerNote && (
                <span className="ml-1.5 text-[11px] text-on-surface-variant">{answerNote}</span>
              )}
            </span>
          </button>
        ) : (
          <div className={cell(true)} aria-current="step">
            <span className="flex items-baseline gap-2">
              <span className={num(true)}>1</span>
              <span className={title(true)}>What is it</span>
            </span>
            <span className="block text-sm text-on-surface-variant mt-0.5">Pick a kind</span>
          </div>
        )}
      </li>
      <li className="flex-1 min-w-0 flex">
        <div className={cell(step === 2)} aria-current={step === 2 ? 'step' : undefined}>
          <span className="flex items-baseline gap-2">
            <span className={num(step === 2)}>2</span>
            <span className={title(step === 2)}>Details</span>
          </span>
          <span className="block text-sm text-on-surface-variant mt-0.5">
            {step === 2 ? 'The rest of it' : 'Depends on the kind'}
          </span>
        </div>
      </li>
    </ol>
  );
};

export interface NewItemModalProps {
  onClose: () => void;
  onMinted: (row: PartsCatalogRow) => void;
  /**
   * Seeds the Name field. Every lane that opens this wizard from an ItemPicker
   * has the text somebody just typed ("New item: “M3x8 screw”…"), and asking
   * them to type it a second time is the kind of small tax that makes people
   * stop using the in-place create.
   *
   * Trimmed and capped to the same 60 the field enforces.
   */
  initialName?: string;
  /**
   * Preselect a kind and open on step two.
   *
   * A preselect, not a lock: step one is still one press away, because a caller
   * guessing from a document is guessing.
   */
  initialKindKey?: string;
  /**
   * Free text the caller already has, usually a line off a scanned document.
   *
   * Shown quietly beside the questions it answers, and used to suggest a
   * commodity class. NOT used to seed the name: a vendor's line description is
   * not what people call the thing.
   */
  hintText?: string;
}

const NewItemModal: React.FC<NewItemModalProps> = ({
  onClose,
  onMinted,
  initialName,
  initialKindKey,
  hintText,
}) => {
  const api = useApi();

  // ---- The kind taxonomy ----------------------------------------------
  // Subscribe to the raw array and derive: the store's own structuredKinds()
  // returns a fresh array per call, so calling it inside a selector re-renders
  // forever.
  const kinds = useItemKindsStore((s) => s.kinds);
  const kindsLoading = useItemKindsStore((s) => s.loading);
  // The store's own answer to "has the list ever arrived", which is what
  // separates a caller's kind that is still in flight from one nothing knows.
  // A count of zero is a real, if unusual, taxonomy.
  const kindsLoaded = useItemKindsStore((s) => s.loaded);
  const kindsError = useItemKindsStore((s) => s.error);
  const loadKinds = useItemKindsStore((s) => s.load);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKinds(api); }, []);

  // ---- Where we are ----------------------------------------------------
  const [step, setStep] = useState<1 | 2>(initialKindKey ? 2 : 1);
  const [kindKey, setKindKey] = useState(initialKindKey || '');
  // Has anybody typed on step two yet? Escape goes BACK when they have, and
  // closes when they have not. Seeded values do not count: somebody who opened
  // the wizard from a picker and immediately pressed Escape meant "close".
  const [dirty, setDirty] = useState(false);
  const [addingVendor, setAddingVendor] = useState(false);

  const kind = useMemo(() => kindByKey(kinds, kindKey), [kinds, kindKey]);
  const isBoard = kind?.code_mode === 'structured';
  // A caller-supplied kind before the taxonomy lands, or one the taxonomy does
  // not know. The two want different screens, so they are not one flag.
  // Three different answers to "the caller named a kind and we cannot show it",
  // because they need three different screens. `loaded` stays FALSE on a failed
  // fetch, so without the error arm the third case renders "Loading…" forever.
  const kindPending = !!kindKey && !kind && !kindsLoaded && !kindsError;
  const kindLoadFailed = !!kindKey && !kind && !kindsLoaded && !!kindsError;
  const kindUnknown = !!kindKey && !kind && kindsLoaded;

  // ---- The item ---------------------------------------------------------
  const [board, setBoard] = useState<BoardFields>(emptyBoard);
  const [codedVendors, setCodedVendors] = useState<{ id: string; code: string | null; name: string }[]>([]);
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);
  // Seeded ONCE, from the initial prop. A later change to `initialName` must not
  // rewrite the field under somebody editing it: the wizard is opened per act,
  // and the text that opened it is the text it starts with.
  const [name, setName] = useState(() => (initialName || '').trim().slice(0, NAME_MAX));
  const [description, setDescription] = useState('');
  const [mpn, setMpn] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [reorderThreshold, setReorderThreshold] = useState('');
  const [isConsumable, setIsConsumable] = useState(false);
  // A part somebody is trying out. On a BOARD this is the SKU builder's own
  // segment, because there it also puts the -P on the code; everything else has
  // no code to carry it, so it is a plain question on the form.
  const [isPrototype, setIsPrototype] = useState(false);
  // '' means "nobody has said yet" on both axes, and it is the honest starting
  // state. The hint text gets to make a first guess, labeled as one.
  const [materialType, setMaterialType] = useState<MaterialType | ''>('');
  const [commodityClass, setCommodityClass] = useState<CommodityClass | ''>(
    () => suggestCommodityClass(hintText) || ''
  );
  const [classGridOpen, setClassGridOpen] = useState(true);
  // Distinguishes "something implied this" from "a person picked it", so the
  // tile can say which. A suggestion that looks like a decision is the thing
  // migration 016 exists to prevent.
  const [classWasSuggested, setClassWasSuggested] = useState(
    () => !!suggestCommodityClass(hintText)
  );

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The Board segment is the kind, not a second copy of it. Derived rather than
  // stored so the two can never disagree; the builder is locked, so nothing
  // else ever writes it.
  const boardFields: BoardFields = useMemo(
    () => (isBoard && kind ? { ...board, board: kind.key } : board),
    [board, isBoard, kind]
  );
  // Derived, never typed. One expression is the only way a SKU string comes
  // into existence on this screen, and only for a board: a short kind's code is
  // the server's to assign.
  const sku = isBoard ? previewBoardSku(boardFields) : '';
  const skuOk = isBoard ? skuIsWellFormed('board', sku) : true;
  const chosenVendor =
    (boardFields.vendorCode && codedVendors.find((v) => v.code === boardFields.vendorCode)) || null;
  const generatedName = isBoard
    ? previewGeneratedName(boardFields, kind?.label || null, chosenVendor?.name || boardFields.vendorCode || null)
    : null;

  // ---- AI prefill state -----------------------------------------------
  const [prefillTab, setPrefillTab] = useState<PrefillTab>('pdf');
  const [prefillUrl, setPrefillUrl] = useState('');
  const [prefillText, setPrefillText] = useState('');
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [prefillResult, setPrefillResult] = useState<ItemPrefillResult | null>(null);
  const [warningsDismissed, setWarningsDismissed] = useState(false);

  // ── IS THIS BOARD ALREADY IN THE CATALOG UNDER ANOTHER NAME? ──────────────
  //
  // SD-ESC-02-2-6-GC and SD-ESC-02-06-GC are one physical board. Both pass the
  // board grammar (revision 2.6 and revision 06), so the builder above cannot
  // refuse the second one and neither can the backend. The only party who can
  // tell revision 2.6 from revision 6 is whoever is reading the silkscreen.
  //
  // So this SHOWS rather than blocks: the other items that are the same board
  // from the same assembler, listed while the SKU is still being composed. Same
  // posture as ERPNext, which surfaces existing items instead of refusing a new
  // one.
  const [siblings, setSiblings] = useState<SkuSibling[]>([]);
  const [siblingsAcknowledged, setSiblingsAcknowledged] = useState(false);
  // An `exact` sibling is a provable duplicate (same stem, tails equal as numbers)
  // rather than a suspicion, and the panel says so in stronger words.
  const exactSibling = siblings.some((sib) => sib.exact);

  // PCBA vendors for the board SKU's manufacturer segment, and the vendor
  // taxonomy that rides along on the same response (see procurement-router.js:
  // the active types ship as `types` on GET /vendors, which is what the vendor
  // FORM reads — /vendor-types is the admin projection and includes retired
  // ones). Failure is tolerated: the builder falls back to a typed two-letter
  // code, so a vendors outage does not block minting a part.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Every vendor with a code, whatever its type: the backend resolves the
        // manufacturer segment by code alone (see useCodedVendors).
        const data = (await api.get('/procurement/vendors')) as {
          vendors: { id: string; code: string | null; name: string }[];
          types?: VendorTypeOption[];
        };
        if (cancelled) return;
        setCodedVendors((data.vendors || []).filter((v) => v.code));
        setVendorTypes(data.types || []);
      } catch {
        // Typed fallback covers it.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A structured kind IS an assembled board, so axis 2 is entailed rather than
  // defaulted, and it opens answered with the grid collapsed. Axis 1 is left
  // alone: a board can be something we make or something we buy in, and only a
  // person knows which.
  //
  // Keyed on the RESOLVED kind, so it fires once when a caller's `initialKindKey`
  // finally matches a loaded row, and again only if somebody steps back and
  // chooses a different kind. The else branch is what makes that reversible:
  // 'pcba' on a grommet is this rule's leftover, not an answer anybody gave.
  const resolvedKindKey = kind?.key || '';
  useEffect(() => {
    if (!resolvedKindKey) return;
    if (isBoard) {
      setCommodityClass('pcba');
      setClassWasSuggested(false);
      setClassGridOpen(false);
    } else {
      setCommodityClass((prev) => (prev === 'pcba' ? '' : prev));
    }
  }, [resolvedKindKey, isBoard]);

  // The Prototype kind answers the Prototype question. Keyed on the kind alone
  // and reversible either way: a tick survives everything except changing what
  // the item IS, and stepping back to a grommet does not leave the flag behind.
  useEffect(() => {
    if (!resolvedKindKey) return;
    setIsPrototype(resolvedKindKey === PROTOTYPE_KIND);
  }, [resolvedKindKey]);

  // Re-asked whenever the composed SKU changes, because the builder's segments
  // change it a character at a time and the answer depends on all of them.
  // Cheap: the endpoint filters on an indexed SKU prefix over a catalog of tens
  // of rows, and it only fires for a well-formed board SKU.
  useEffect(() => {
    if (!isBoard || !skuOk) {
      setSiblings([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = (await api.get(
          `/parts-catalog/sku-siblings?sku=${encodeURIComponent(sku)}`
        )) as { rows: SkuSibling[] };
        if (cancelled) return;
        setSiblings(data.rows || []);
        // A NEW SKU IS A NEW QUESTION. Carrying the acknowledgement across an
        // edit would let somebody tick it on a harmless proposal and then compose
        // a genuine duplicate with the confirmation already satisfied.
        setSiblingsAcknowledged(false);
      } catch {
        // Additive: a failed lookup must not stop somebody minting an item.
        if (!cancelled) setSiblings([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku, skuOk, isBoard]);

  // ---- Step one: the kind picker ---------------------------------------

  const chooseKind = (key: string) => {
    setKindKey(key);
    setFormError(null);
    setStep(2);
  };

  // ---- Prefill ---------------------------------------------------------

  const runPrefill = async (body: FormData | { url: string } | { text: string }) => {
    if (prefillLoading) return;
    setPrefillLoading(true);
    setPrefillError(null);
    try {
      const result = (await api.post('/procurement/item-prefill', { body })) as ItemPrefillResult;
      // A read that landed is work, and it cost a model call. Escape must step
      // back to the kind rather than throw the answers away.
      touch();
      setPrefillResult(result);
      setWarningsDismissed(false);
      setDescription(result.fields.description.value === null ? '' : String(result.fields.description.value));
      setMpn(result.fields.mpn.value === null ? '' : String(result.fields.mpn.value));
      setManufacturer(
        result.fields.manufacturer.value === null ? '' : String(result.fields.manufacturer.value)
      );
      setIsConsumable(result.fields.is_consumable.value === true);
      // This lane exists for raw materials and off-the-shelf parts only, so a
      // successful read always means ROH on axis 1. Both setters SUGGEST rather
      // than assign: they fill only when nobody has picked yet. A human who
      // already chose Battery and then drops a datasheet must not have that
      // answer replaced by inference, least of all silently — the pickers sit
      // well below the prefill panel, so on a laptop the overwrite would happen
      // off-screen. Human declaration outranks the model.
      setMaterialType((prev) => prev || 'raw_material');

      const guessed = commodityClassFromPrefill(result);
      // Collapse the grid and label the tile, same as the description-driven
      // suggestion does. Without this the AI's pick sat in an open grid with
      // nothing saying where it came from, so it read as a human decision.
      //
      // These two run OUTSIDE the setCommodityClass updater. Putting them
      // inside it makes the updater impure, and React is allowed to call an
      // updater more than once. The data guard stays in the updater below,
      // where it belongs.
      if (guessed && !commodityClass) setClassWasSuggested(true);
      setCommodityClass((prev) => prev || guessed);
    } catch (err: any) {
      // The endpoint's error shape is flat ({error, hint}), not the nested
      // {error:{code,message}} its neighbors use — hint is written to be
      // shown verbatim, so read it directly rather than through err.message.
      setPrefillError(err?.data?.hint || 'Could not read that. Fill in the fields by hand.');
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleFile = (file: File | null | undefined) => {
    if (!file || prefillLoading) return;
    const fd = new FormData();
    fd.append('file', file);
    runPrefill(fd);
  };

  const handleUrlSubmit = () => {
    if (!prefillUrl.trim() || prefillLoading) return;
    runPrefill({ url: prefillUrl.trim() });
  };

  const handleTextSubmit = () => {
    if (!prefillText.trim() || prefillLoading) return;
    runPrefill({ text: prefillText.trim() });
  };

  const descMeta = prefillResult?.fields.description;
  const mpnMeta = prefillResult?.fields.mpn;
  const mfrMeta = prefillResult?.fields.manufacturer;
  const consMeta = prefillResult?.fields.is_consumable;
  const notSure = prefillResult?.not_sure || [];
  const descNotSure = notSure.includes('description');
  const mpnNotSure = notSure.includes('mpn');
  const mfrNotSure = notSure.includes('manufacturer');
  const consNotSure = notSure.includes('is_consumable');

  // ---- Create ----------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || step !== 2) return;
    setFormError(null);

    if (!kind) {
      setFormError('Pick what sort of thing this is first.');
      setStep(1);
      return;
    }
    if (isBoard && !skuOk) {
      // The amber note under the code already names the missing segments; the
      // banner points at it rather than saying the same sentence twice.
      setFormError('Finish the board code first. The note under it says what is missing.');
      return;
    }
    // A board can generate its own name from its segments, and the preview
    // under the field has been showing exactly what. Nothing else can, so
    // nothing else may skip it.
    if (!isBoard && !name.trim()) {
      setFormError('Give it a name. It is what every other screen will show, and only a board can generate its own.');
      return;
    }
    if (!materialType) {
      setFormError('Pick a build stage. It says where this sits in what we make, and nothing guesses it for you.');
      return;
    }
    if (!commodityClass) {
      setFormError('Pick an item type. It says what kind of thing this is, and nothing guesses it for you.');
      return;
    }

    const body: Record<string, unknown> = {
      kind_key: kind.key,
      material_type: materialType,
      commodity_class: commodityClass,
    };
    if (isBoard) {
      // The SKU AND the attributes it is composed from. The server can parse a
      // board SKU back into these, but it should not have to: this screen knows
      // the answers because a person just gave them one segment at a time.
      body.sku = sku.trim();
      const generation = parseInt(boardFields.generation, 10);
      if (Number.isFinite(generation)) body.platform_generation = generation;
      if (boardFields.revMajor) {
        body.revision = boardFields.revMinor
          ? `${boardFields.revMajor}.${boardFields.revMinor}`
          : boardFields.revMajor;
      }
      if (boardFields.variant) body.variant = `V${boardFields.variant}`;
      if (chosenVendor) body.pcba_vendor_id = chosenVendor.id;
      body.is_prototype = boardFields.prototype;
    }
    if (name.trim()) body.name = name.trim();
    if (description.trim()) body.description = description.trim();
    if (mpn.trim()) body.mpn = mpn.trim();
    if (manufacturer.trim()) body.manufacturer = manufacturer.trim();
    if (reorderThreshold.trim()) {
      const n = parseInt(reorderThreshold, 10);
      if (!Number.isInteger(n) || n < 0) {
        setFormError('Reorder threshold must be a whole number, 0 or more.');
        return;
      }
      body.reorder_threshold = n;
    }
    body.is_consumable = isConsumable;
    // The board branch above took this from the SKU builder's segment, which is
    // the same fact spelled as a code. Here there is no code, so the checkbox is.
    if (!isBoard) body.is_prototype = isPrototype;

    setSubmitting(true);
    try {
      const row = (await api.post('/parts-catalog', { body })) as PartsCatalogRow;
      onMinted(row);
    } catch (err: any) {
      // Every mint refusal the server has (duplicate_sku, sku_required,
      // unknown_short_stem, kind_mismatch, unknown_board_code) arrives with a
      // sentence written to be read by the person who caused it. Showing that
      // sentence is the whole handling; a per-code branch here would only
      // reword an answer the server already phrased better.
      setFormError(
        err?.data?.error?.message || err?.message || 'Could not create the item.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Anything the person typed marks step two dirty, which is what decides
  // whether Escape steps back or closes.
  const touch = () => { if (!dirty) setDirty(true); };

  // Typed work, for the Dialog's discard question: the fields against what the
  // wizard opened with. Separate from `dirty` above, which is a touched flag that
  // decides back-versus-close. The class and the prototype tick are also written
  // by the kind itself, so only a value the kind does not explain counts. The
  // board's Board segment is the kind's, written back by every segment edit.
  // The embedded vendor form keeps its fields to itself, so being open counts.
  const [openedKind] = useState(kindKey);
  const [openedName] = useState(name);
  const [openedClass] = useState(commodityClass);
  const hasTypedWork =
    kindKey !== openedKind ||
    addingVendor ||
    name !== openedName ||
    description !== '' ||
    mpn !== '' ||
    manufacturer !== '' ||
    reorderThreshold !== '' ||
    isConsumable ||
    materialType !== '' ||
    prefillUrl !== '' ||
    prefillText !== '' ||
    prefillResult !== null ||
    JSON.stringify({ ...board, board: '' }) !== JSON.stringify({ ...emptyBoard(), board: '' }) ||
    isPrototype !== (resolvedKindKey === PROTOTYPE_KIND) ||
    (commodityClass !== openedClass && !classWasSuggested && !(isBoard && commodityClass === 'pcba'));

  const goBack = () => {
    setStep(1);
    setFormError(null);
  };

  const hint = (hintText || '').trim();

  // ONE banner, both steps. A caller-preselected kind lands on step two, where
  // a failed taxonomy fetch is exactly as blocking and exactly as retryable.
  const kindsErrorBanner = (
    <Alert
      tone="warning"
      live="alert"
      action={
        <Button variant="quiet" size="sm" onClick={() => loadKinds(api, true)} disabled={kindsLoading} className="shrink-0">
          {kindsLoading ? 'Trying…' : 'Try again'}
        </Button>
      }
    >
      {kindsError}
    </Alert>
  );

  // ---- Focus follows the step ------------------------------------------
  //
  // Dialog moves focus once, at open, and a step change is a whole new
  // set of questions with the old field unmounted: without this, focus falls to
  // the body and a keyboard user has to tab in from the top of the dialog.
  //
  // Same two-tier rule Dialog uses, so the marker means one thing
  // everywhere: a field that names itself with `data-drawer-autofocus` wins,
  // otherwise the first control in the step. Deliberately not Dialog's own
  // fallback, which is the whole panel.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // After Dialog's own open focus. On open the two agree anyway.
    const t = setTimeout(() => {
      const root = bodyRef.current;
      if (!root) return;
      const marked = root.querySelector<HTMLElement>('[data-drawer-autofocus]');
      const first = root.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      (marked || first)?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [step]);

  // ---- Render ----------------------------------------------------------

  return (
    <Dialog open title="New item" onClose={onClose} size="xl" busy={submitting} dirty={hasTypedWork}>
      <StepRail
        step={step}
        answer={kind?.label || kindKey || null}
        answerNote={kind ? (isBoard ? 'Board code' : 'Short code') : null}
        onBack={goBack}
      />

      <div ref={bodyRef}>
      {step === 1 ? (
        <div className="space-y-4">
          <div>
            <KindPicker
              label="What is it?"
              autoFocus
              value={kindKey || null}
              onChange={(chosen) => chooseKind(chosen.key)}
              partNumberHint={mpn.trim() || null}
            />
            <p className="text-[11px] text-on-surface-variant mt-1.5">
              A board gets the full code, built segment by segment. Everything else gets a short
              code assigned when you create it, and the name does the talking.
            </p>
          </div>

          {/* What the person is classifying, kept beside the question. */}
          <Reveal show={!!hint}>
            <p className="text-[11px] text-on-surface-variant">
              From the document: <span className="text-on-surface-variant">{hint}</span>
            </p>
          </Reveal>

          {/* No banner here: KindPicker says so itself and carries the retry,
              so step one has exactly one place the failure is reported. The
              banner below is step TWO's, where the picker is not mounted. */}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={buttonClass('quiet')}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : kindPending ? (
        <SkeletonGroup label="Loading item kinds" className="space-y-3 py-6">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-10 w-full" />
        </SkeletonGroup>
      ) : kindLoadFailed ? (
        // The kind is probably fine; the list of kinds is what did not arrive,
        // and there is nothing on this step that can be answered without it.
        <div className="space-y-3 py-4">
          {kindsErrorBanner}
          <p className="text-sm text-on-surface-variant">
            The questions on this step depend on what sort of thing this is, and that list is
            what did not load.
          </p>
          <button
            type="button"
            onClick={goBack}
            className={buttonClass('quiet')}
          >
            Back to kinds
          </button>
        </div>
      ) : kindUnknown ? (
        <div className="space-y-3 py-4">
          <p className="text-sm text-on-surface-variant">
            There is no kind called <span className="font-mono text-on-surface">{kindKey}</span> any
            more. Pick one that exists, or add it.
          </p>
          <button
            type="button"
            onClick={goBack}
            className={buttonClass('quiet')}
          >
            Back to kinds
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          // Escape means "undo the smallest thing". With work on the form that
          // is stepping back to the question that shaped it; with an untouched
          // form there is nothing to undo, so it closes, which is what
          // Dialog does when this leaves the key alone.
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            // Firefox bubbles Escape after a native select consumes it, the
            // same case Dialog guards.
            if (e.target instanceof HTMLSelectElement) return;
            if (!dirty) return;
            e.stopPropagation();
            goBack();
          }}
          // NO ANIMATION ON THIS ELEMENT. `animate-rowLand` fills `both`, so it
          // leaves `transform: translateY(0)` on the node forever, and a
          // transformed ancestor becomes the containing block for every `fixed`
          // descendant. The classification guide's HelpDrawer is one of those:
          // its backdrop would stop at this form's box instead of covering the
          // page. The landing moment lives on the step rail, which contains
          // nothing fixed.
          className="space-y-4"
        >
          {/* ---------------- AI prefill, short kinds only ---------------- */}
          {!isBoard && (
            <div className="space-y-3 rounded-lg bg-surface-container-low p-4">
              <div>
                <p className="text-sm font-medium text-on-surface">Fill this in from a datasheet</p>
                <p className="text-xs text-on-surface-variant mt-0.5">
                  Drop a datasheet PDF, paste a product link, or paste the page text. You still
                  review and confirm everything before it saves.
                </p>
              </div>

              {/* A segmented control, not three loose pills: the three are one
                  choice, and the track says so. */}
              <SegTrack
                label="Fill in from"
                value={prefillTab}
                onChange={(key) => {
                  setPrefillTab(key as PrefillTab);
                  setPrefillError(null);
                }}
                options={[
                  { key: 'pdf', label: 'Datasheet PDF' },
                  { key: 'url', label: 'Product link' },
                  { key: 'text', label: 'Paste text' },
                ]}
              />

              <FadeIn key={prefillTab} className="space-y-3">
              {prefillTab === 'pdf' && (
                <DropZone
                  what="a datasheet PDF"
                  accept=".pdf,application/pdf"
                  size="sm"
                  busy={prefillLoading ? 'Reading the document…' : false}
                  onFiles={(files) => handleFile(files[0])}
                  inputProps={{ 'aria-label': 'Datasheet PDF' }}
                />
              )}

              {prefillTab === 'url' && (
                <div className="flex gap-2">
                  <Field
                    type="url"
                    value={prefillUrl}
                    onChange={(e) => { touch(); setPrefillUrl(e.target.value); }}
                    placeholder="https://www.example.com/product/..."
                    aria-label="Product link"
                    disabled={prefillLoading}
                    // Enter here means "read this page", not "create the item".
                    // Without this the keypress reaches the form around it and
                    // submits it, which on a half-filled form is a validation
                    // error and on a full one mints an item from a URL nobody
                    // read yet. The paste-text lane is a textarea and keeps its
                    // newline.
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      handleUrlSubmit();
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleUrlSubmit}
                    disabled={prefillLoading || !prefillUrl.trim()}
                    className={`${buttonClass('filled')} shrink-0`}
                  >
                    Fill in
                  </button>
                </div>
              )}

              {prefillTab === 'text' && (
                <div className="space-y-2">
                  <TextArea
                    value={prefillText}
                    onChange={(e) => { touch(); setPrefillText(e.target.value); }}
                    rows={4}
                    placeholder="Paste the product description and specs from the page"
                    aria-label="Paste text"
                    disabled={prefillLoading}
                  />
                  <button
                    type="button"
                    onClick={handleTextSubmit}
                    disabled={prefillLoading || !prefillText.trim()}
                    className={buttonClass('filled')}
                  >
                    Fill in
                  </button>
                </div>
              )}
              </FadeIn>

              <Reveal show={prefillLoading && prefillTab !== 'pdf'}>
                <p className="text-xs text-on-surface-variant">Reading the document…</p>
              </Reveal>

              <Alert show={!!prefillError} tone="warning" live="alert">{prefillError}</Alert>

              <Alert
                show={!!prefillResult && prefillResult.warnings.length > 0 && !warningsDismissed}
                tone="warning"
                onDismiss={() => setWarningsDismissed(true)}
                dismissLabel="Dismiss warnings"
              >
                <div className="space-y-1">
                  {prefillResult?.warnings.map((w, i) => (
                    <p key={i}>{w}</p>
                  ))}
                </div>
              </Alert>
            </div>
          )}

          {!isBoard && (
            <div className="flex items-center gap-2 text-[11px] text-on-surface-variant">
              <div className="h-px flex-1 bg-outline-variant" />
              or fill in by hand
              <div className="h-px flex-1 bg-outline-variant" />
            </div>
          )}

          {/* ---------------- The board's code ---------------- */}
          {isBoard && (
            <>
              <SkuBuilder
                shape="board"
                onShapeChange={noop}
                board={boardFields}
                onBoardChange={(next) => { touch(); setBoard(next); }}
                nonBoard={NON_BOARD_UNUSED}
                onNonBoardChange={noop}
                codedVendors={codedVendors}
                labelClass={labelClass}
                lockBoard
                onNewVendor={() => setAddingVendor(true)}
              />

              {/* IN PLACE, not in a second drawer: this is already inside one,
                  and two focus traps over one Escape key is not a flow. */}
              {addingVendor && (
                <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3">
                  <p className="text-sm font-medium text-on-surface">New vendor</p>
                  <p className="text-xs text-on-surface-variant -mt-2">
                    The assembler that builds this board. Give it a two-letter code and it becomes
                    the manufacturer segment of the code above.
                  </p>
                  <VendorForm
                    embedded
                    mode="create"
                    types={vendorTypes}
                    defaultType="pcba"
                    onClose={() => setAddingVendor(false)}
                    onSaved={(saved?: VendorFullRow) => {
                      setAddingVendor(false);
                      if (!saved) return;
                      // The vendor exists either way; the code is what a board
                      // SKU can carry. Saying so beats a form that closes and
                      // changes nothing.
                      if (!saved.code) {
                        setFormError(
                          `${saved.name} was created, but a board code carries a two-letter `
                          + 'vendor code and this one has none. Add one under Vendors, then pick it here.'
                        );
                        return;
                      }
                      // Straight back into the segment that asked for it, and
                      // into the list behind it, so the option is there rather
                      // than arriving after a refetch.
                      setCodedVendors((prev) =>
                        prev.some((v) => v.id === saved.id)
                          ? prev
                          : [...prev, { id: saved.id, code: saved.code, name: saved.name }]
                      );
                      // A vendor that now exists in the database is the most
                      // expensive thing on this screen to have to redo.
                      touch();
                      setBoard((b) => ({ ...b, vendorCode: saved.code as string }));
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* ---------------- The short kind's code ---------------- */}
          {!isBoard && kind && (
            <p className="text-[11px] text-on-surface-variant">
              Code: assigned when you create it, like{' '}
              <span className="font-mono text-on-surface-variant">{exampleCode(kind.key, 'short')}</span>
            </p>
          )}

          {/* NAME. It is what the receiving hub, the PO line, the scan screen
              and every toast will say. */}
          <div>
            <label className="block">
              <span className={labelClass}>{isBoard ? 'Name (optional)' : 'Name (required)'}</span>
              <Field
                value={name}
                maxLength={NAME_MAX}
                // The one field a short kind cannot do without, so it is the
                // one focus lands on. Dialog reads this marker rather than
                // autoFocus, which loses the race against its own mount effect.
                {...(!isBoard ? { 'data-drawer-autofocus': true } : {})}
                placeholder="What do people call this?"
                onChange={(e) => {
                  const next = e.target.value;
                  touch();
                  setName(next);
                  setFormError(null);
                  // The same suggester the description drives, now fed by the
                  // field people actually fill: somebody typing "M3x8 screw"
                  // lands on Hardware/fastener without reading the tile grid.
                  // Suggestion only, and only while the axis is still a guess.
                  if (!commodityClass || classWasSuggested) {
                    const guess = suggestCommodityClass(next);
                    if (guess) {
                      setCommodityClass(guess);
                      setClassWasSuggested(true);
                    }
                  }
                }}
              />
            </label>
            {isBoard && (
              <div className="mt-1.5 text-[11px]">
                {generatedName ? (
                  <p className="text-on-surface-variant">
                    Will show as:{' '}
                    <span className="text-on-surface">
                      {joinName(name.trim() || generatedName.base, generatedName.detail)}
                    </span>
                    {!name.trim() && <span className="text-on-surface-variant">. Leave empty to use this.</span>}
                  </p>
                ) : (
                  <p className="text-on-surface-variant">
                    Fill in the code above and this will show the name it generates.
                  </p>
                )}
              </div>
            )}
            {!isBoard && hint && (
              <p className="text-[11px] text-on-surface-variant mt-1.5">
                From the document: <span className="text-on-surface-variant">{hint}</span>
              </p>
            )}
          </div>

          {/* A callout, so it steps up rather than draws a box.
              THE TWO CASES ARE NOT THE SAME WEIGHT, and the panel says so. A
              provable duplicate is a hard stop, and a hard stop that looks
              exactly like "worth a look" is a hard stop people walk past — so
              the exact case takes a red ring, which is emphasis in a semantic
              hue rather than a box drawn round a container. The soft case stays
              plain and lets its amber heading carry it. */}
          <Reveal show={siblings.length > 0}>
            <div
              className={`space-y-2 rounded-lg bg-surface-container-low p-3${
                exactSibling ? ' ring-1 ring-inset ring-red-500/40' : ''
              }`}
            >
              {/* TWO DIFFERENT CLAIMS, SAID DIFFERENTLY. A shared board and assembler
                  is a question, because a revision bump looks identical. An equal
                  numeric tail is an answer: FF-RTK-02-2 and FF-RTK-02-02 are the same
                  number written two ways and there is nothing to weigh. */}
              <p className={`text-xs font-semibold ${exactSibling ? 'text-red-300' : 'text-amber-300'}`}>
                {exactSibling
                  ? 'This is the same code, spelled differently'
                  : siblings.length === 1
                    ? 'This board is already in the catalog once'
                    : `This board is already in the catalog ${siblings.length} times`}
              </p>
              <p className="text-[11px] text-on-surface-variant leading-relaxed">
                {exactSibling
                  ? 'Only the leading zeros differ, so this would be a second code for one item. That splits its stock in two, and the string cannot be changed once anything is ordered against it.'
                  : 'Same board, same generation, same assembler. If one of these is the item you mean, use it: a second code for one physical board splits its stock, and the string cannot be changed once anything is ordered against it.'}
              </p>
              <ul className="space-y-1">
                {siblings.map((sib) => (
                  <li key={sib.id} className="text-[11px]">
                    {/* Same renderer as the merge dialog: the sibling endpoint
                        returns the name fields, and a person deciding "is this the
                        same board" reads names, with the code beneath. */}
                    <ItemName size="sm" showSku item={sib} />
                  </li>
                ))}
              </ul>
              {/* A CHECKBOX, NOT A REFUSAL. Revision bumps are legitimate and
                  common, and the grammar cannot tell one from a duplicate, so the
                  person reading the silkscreen decides. `m-0` because the global
                  input rule in index.css hits checkboxes. */}
              <Checkbox
                size="sm"
                checked={siblingsAcknowledged}
                onChange={(e) => setSiblingsAcknowledged(e.target.checked)}
                label={
                  exactSibling
                    ? 'I know, and I want a separate item anyway.'
                    : 'This is a different revision, not one of those.'
                }
              />
            </div>
          </Reveal>

          <div>
            <label className="block">
              <span className={labelClass}>Notes (optional)</span>
              <Field
                warn={!descNotSure && fieldNeedsReview(descMeta)}
                value={description}
                onChange={(e) => {
                  const next = e.target.value;
                  touch();
                  setDescription(next);
                  // Same suggester the scan card uses. Only ever fills an
                  // UNANSWERED axis, and only while the answer is still a guess:
                  // the moment a person picks a tile, classWasSuggested goes
                  // false and typing stops moving it under them.
                  if (!commodityClass || classWasSuggested) {
                    const guess = suggestCommodityClass(next);
                    if (guess) {
                      setCommodityClass(guess);
                      setClassWasSuggested(true);
                    }
                  }
                }}
                placeholder="Anything worth knowing that the name does not say"
              />
            </label>
            <FieldReviewNote notSure={descNotSure} needsReview={!descNotSure && fieldNeedsReview(descMeta)} />
          </div>

          {/* Manufacturer and MPN sit together because they are ONE fact in two
              halves. "RC0402FR-0710KL" is ambiguous on its own; "Yageo
              RC0402FR-0710KL" is a part you can buy. Splitting them across the
              form would invite filling in one. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block">
                <span className={labelClass}>Manufacturer</span>
                <Field
                  warn={!mfrNotSure && fieldNeedsReview(mfrMeta)}
                  value={manufacturer}
                  onChange={(e) => { touch(); setManufacturer(e.target.value); }}
                  placeholder="Who makes it, not who sells it"
                />
              </label>
              <FieldReviewNote
                notSure={mfrNotSure}
                needsReview={!mfrNotSure && fieldNeedsReview(mfrMeta)}
                notSureText="The datasheet names a distributor, not a manufacturer."
              />
            </div>
            <div>
              <label className="block">
                <span className={labelClass}>MPN</span>
                <Field
                  warn={!mpnNotSure && fieldNeedsReview(mpnMeta)}
                  value={mpn}
                  onChange={(e) => { touch(); setMpn(e.target.value); }}
                  placeholder="Manufacturer's part number"
                />
              </label>
              {/* Outside the label: a note is a description, not part of the
                  field's accessible name. */}
              <FieldReviewNote notSure={mpnNotSure} needsReview={!mpnNotSure && fieldNeedsReview(mpnMeta)} />
            </div>
          </div>

          <label className="block">
            <span className={labelClass}>Reorder threshold</span>
            <Field
              type="number"
              min={0}
              value={reorderThreshold}
              onChange={(e) => { touch(); setReorderThreshold(e.target.value); }}
              placeholder="Tell me when stock drops below"
            />
          </label>

          {/* The definitions, one click away and shared with every other mint
              surface. Above the pair rather than under one of them: it explains
              both axes, and the boundary cases people stall on cut across them. */}
          <div className="flex justify-end -mb-1">
            <ClassificationGuideButton />
          </div>

          {/* Axis 1: where in the build chain. Five options, so a select keeps
              it to one tab stop and leaves the tiles below for the axis people
              actually browse by. The SAP code rides in the label on purpose:
              it is the word that works outside this app. */}
          <div>
            <label className={labelClass} htmlFor="new-item-material-type">
              Build stage (required)
            </label>
            <Select
              id="new-item-material-type"
              label="Build stage (required)"
              value={materialType}
              placeholder="Select…"
              onChange={(key) => {
                touch();
                setMaterialType(key as MaterialType | '');
                setFormError(null);
              }}
              options={[
                { key: '', label: 'Select…' },
                ...MATERIAL_TYPES.map((t) => ({ key: t, label: MATERIAL_TYPE_META[t].label })),
              ]}
            />
            <p className="text-[11px] text-on-surface-variant mt-1">
              {materialType
                ? MATERIAL_TYPE_META[materialType].hint
                : 'Where this sits in what we make. Nothing fills this in for you.'}
            </p>
          </div>

          {/* Axis 2: what kind of thing. Tiles rather than a select because this
              is the one people scan, and each option earns its explanation. */}
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <span className={labelClass}>Item type (required)</span>
              <Appear show={!!commodityClass && !classGridOpen && !classWasSuggested}>
                <button
                  type="button"
                  onClick={() => setClassGridOpen(true)}
                  className={buttonClass('quiet', 'sm')}
                >
                  Change
                </button>
              </Appear>
            </div>

            {/* ELEVEN TILES IS A WALL, and it is the single heaviest thing on
                this form. Once a class is CHOSEN there is no reason to keep
                showing the other ten — collapse to the one, with a way back.

                A SUGGESTION leaves the grid open, deliberately: it is visibly
                chosen, labeled as a guess, and one click from wrong. Migration
                016 is explicit that neither axis may have a DB default, and this
                respects that. */}
            <FadeIn key={commodityClass && !classGridOpen && !classWasSuggested ? 'chosen' : 'grid'}>
            {commodityClass && !classGridOpen && !classWasSuggested ? (
              <button
                type="button"
                onClick={() => setClassGridOpen(true)}
                // Still the selected option, just the only one rendered. Without
                // aria-pressed a screen reader hears a button with a class name
                // on it and no indication it is the current answer.
                aria-pressed="true"
                className="w-full text-left rounded border border-primary bg-primary/10 px-3 py-2 transition-colors hover:bg-primary/15"
              >
                <span className="block text-sm font-medium text-on-surface">
                  {COMMODITY_CLASS_META[commodityClass].label}
                </span>
                <span className="block text-[11px] text-on-surface-variant mt-0.5">
                  {COMMODITY_CLASS_META[commodityClass].hint}
                </span>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2" role="group" aria-label="Item type">
                {COMMODITY_CLASSES.map((c, i) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      touch();
                      setCommodityClass(c);
                      setClassGridOpen(false);
                      setClassWasSuggested(false);
                      setFormError(null);
                    }}
                    aria-pressed={commodityClass === c}
                    // Odd count in a two-wide grid: the last tile fills the row
                    // rather than leaving a hole beside it.
                    className={`rounded-lg px-3 py-2 text-left transition-colors duration-150 ease-emphasized motion-reduce:transition-none ${
                      i === COMMODITY_CLASSES.length - 1 && COMMODITY_CLASSES.length % 2 === 1
                        ? 'col-span-2'
                        : ''
                    } ${
                      commodityClass === c
                        ? 'bg-primary/15 ring-2 ring-inset ring-primary/40'
                        : 'bg-surface-container-low hover:bg-surface-container'
                    }`}
                  >
                    <span className="block text-sm font-medium text-on-surface">
                      {COMMODITY_CLASS_META[c].label}
                      {/* The guess wears its provenance. Highlighted like a
                          selection, labeled so nobody mistakes it for one
                          somebody made, and sitting in the open grid so the
                          other ten are right there to disagree with. */}
                      {commodityClass === c && classWasSuggested && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300/90 font-normal">
                          a guess, check it
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-on-surface-variant mt-0.5">
                      {COMMODITY_CLASS_META[c].hint}
                    </span>
                  </button>
                ))}
              </div>
            )}
            </FadeIn>
          </div>

          {/* Consumable is behavior, not identity: does using it deplete
              stock. A board is a unit that gets built into a drone, so the
              question is only asked where it has two answers. */}
          {!isBoard && (
            <div>
              <Checkbox
                checked={isConsumable}
                onChange={(e) => { touch(); setIsConsumable(e.target.checked); }}
                label="Consumable"
              />
              <FieldReviewNote notSure={consNotSure} needsReview={!consNotSure && fieldNeedsReview(consMeta)} />
            </div>
          )}

          {/* Prototype is identity, not behavior: it changes what every screen
              CALLS this item rather than what stock does when you use it. A
              board carries the same answer as a segment of its code, which is
              why the question only appears on this shape. */}
          {!isBoard && (
            <div>
              <Checkbox
                checked={isPrototype}
                onChange={(e) => { touch(); setIsPrototype(e.target.checked); }}
                label="Prototype"
              />
              <p className="text-[11px] text-on-surface-variant mt-1">
                A part you are trying out. It shows as a prototype everywhere and can be
                retired from its own page when you are done.
              </p>
            </div>
          )}

          <Alert show={!!formError} tone="error">{formError}</Alert>

          <div className="flex justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={goBack}
              className={buttonClass('quiet')}
            >
              Back
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className={buttonClass('quiet')}
              >
                Cancel
              </button>
              <button
                type="submit"
                // The acknowledgement gates the button rather than the submit
                // handler, so the reason is visible instead of arriving as an
                // error after a click. Same for the vendor sub-form: while it is
                // open, the thing being created is the vendor.
                disabled={
                  submitting || addingVendor || (siblings.length > 0 && !siblingsAcknowledged)
                }
                // No title: it would replace the button's accessible name
                // (web/CLAUDE.md), and the reason is already on screen.
                className={buttonClass('filled')}
              >
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      )}
      </div>
    </Dialog>
  );
};

export default NewItemModal;
